import type { ImgHTMLAttributes } from 'react';
import { cn } from './cn';

/**
 * Remote images, delivered at the size they are actually displayed.
 *
 * The covers on this site are uploaded straight from the admin and served raw:
 * measured on the live catalogue, a single cover is a **1.3–2.2 MB PNG at
 * 1536px wide, rendered into a 276px box**. Five cards came to 9.5 MB; the full
 * catalogue is an order of magnitude worse. On a Nigerian mobile connection that
 * is the difference between a page that works and one that does not — and it is
 * why cards sat on a flat tint for seconds before their image appeared.
 *
 * Cloudinary can fix this entirely from the delivery URL, no re-upload needed:
 * inserting `f_auto,q_auto,c_fill,w_N` after `/upload/` returns a WebP or AVIF,
 * cropped and scaled to the box, typically 20–60 kB. This component rewrites the
 * URL, emits a `srcSet` for 1x/2x screens, and always sets `width`/`height` so
 * the grid reserves the space and nothing shifts as images land.
 *
 * Non-Cloudinary URLs pass through untouched.
 *
 * Sizing is always the caller's responsibility — pass the height/width classes
 * the slot needs (`h-full w-full` inside an aspect-ratio box, `h-12 w-12` for an
 * avatar). This component only guarantees `object-cover`.
 */

const CLOUDINARY_UPLOAD = /\/image\/upload\//;

/** Widths we request. Anything else is rounded up to the next step. */
const STEPS = [160, 240, 320, 480, 640, 960, 1280];

const stepFor = (width: number): number => STEPS.find((step) => step >= width) ?? STEPS[STEPS.length - 1];

/**
 * Rewrites a Cloudinary delivery URL to serve a right-sized, modern-format image.
 * `c_fill` with `g_auto` crops to the box around the most interesting region
 * rather than the geometric centre.
 */
export const cdnUrl = (url: string | undefined, width: number, height?: number): string | undefined => {
  if (!url || !CLOUDINARY_UPLOAD.test(url)) return url;
  const w = stepFor(width);
  const crop = height
    ? `c_fill,g_auto,w_${w},h_${Math.round((height / width) * w)}`
    : `c_limit,w_${w}`;
  return url.replace(CLOUDINARY_UPLOAD, `/image/upload/f_auto,q_auto,${crop}/`);
};

export interface ImgProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'width' | 'height'> {
  src?: string;
  /** CSS pixel width of the box the image fills. Drives the requested size. */
  width: number;
  height: number;
  /** Above-the-fold images should be eager and high priority. */
  priority?: boolean;
  /** Rendered when `src` is missing — a fallback mark, usually. */
  fallback?: React.ReactNode;
}

export const Img = ({ src, width, height, priority = false, fallback, className, alt = '', ...props }: ImgProps) => {
  if (!src) return <>{fallback ?? null}</>;

  const src1x = cdnUrl(src, width, height);
  const src2x = cdnUrl(src, width * 2, height * 2);

  return (
    <img
      src={src1x}
      // Only worth a srcSet when the URL is actually transformable.
      srcSet={src1x !== src2x ? `${src1x} 1x, ${src2x} 2x` : undefined}
      width={width}
      height={height}
      alt={alt}
      loading={priority ? 'eager' : 'lazy'}
      decoding={priority ? 'sync' : 'async'}
      {...(priority ? { fetchpriority: 'high' } : {})}
      /* Only `object-cover` is forced. Sizing is the caller's, because `cn` is a
         plain class joiner, not tailwind-merge: a base `h-full w-full` here does
         not lose to a caller's `h-12 w-12`, it fights it in the stylesheet — which
         is exactly how a 48px avatar rendered at 700px. */
      className={cn('object-cover', className)}
      {...props}
    />
  );
};

export default Img;
